import { get, set, del } from 'idb-keyval';

/**
 * Coffre IndexedDB chiffré pour le refresh token Harmaury.
 *
 * Stratégie Phase 1 :
 * - Une clé AES-GCM 256 bits est générée au premier usage et stockée comme
 *   CryptoKey *non extractible* dans IndexedDB (les CryptoKey non extractibles
 *   ne peuvent jamais être lues par JS, même via XSS, ce qui réduit le risque
 *   par rapport à localStorage en clair).
 * - Le refresh token est chiffré avec cette clé + IV aléatoire 12 octets et
 *   stocké dans IndexedDB (`harmaury.auth.refresh`).
 * - L'access token reste *uniquement en mémoire* via le store Zustand, jamais
 *   persisté.
 *
 * Limites connues : un attaquant capable d'exécuter du JS dans l'origine peut
 * toujours appeler `decryptRefreshToken()` (la clé n'est pas extractible mais
 * la fonction de déchiffrement existe). Ce schéma reste préférable au
 * localStorage en clair car (a) le token n'est pas lisible par un screen
 * scraper trivial, (b) le matériel cryptographique n'est jamais exposé. Pour
 * Phase 2+, envisager WebAuthn ou une rotation côté serveur plus agressive.
 */

const DB_KEY_REFRESH = 'harmaury.auth.refresh.v1';
const DB_KEY_CRYPTO = 'harmaury.auth.cryptokey.v1';

interface EncryptedPayload {
  iv: number[];
  ciphertext: number[];
}

function isCryptoKey(value: unknown): value is CryptoKey {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'algorithm' in value &&
    'usages' in value
  );
}

function assertWebCryptoAvailable(): SubtleCrypto {
  if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') {
    throw new Error('WebCrypto indisponible — environnement non sécurisé.');
  }
  return crypto.subtle;
}

let inMemoryKey: CryptoKey | null = null;

async function getOrCreateKey(): Promise<CryptoKey> {
  const subtle = assertWebCryptoAvailable();
  if (inMemoryKey !== null) {
    return inMemoryKey;
  }
  try {
    const existing = await get(DB_KEY_CRYPTO);
    if (isCryptoKey(existing)) {
      inMemoryKey = existing;
      return existing;
    }
  } catch {
    // IndexedDB indisponible : on dérive une clé éphémère in-memory.
  }
  const key = await subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non extractible — impossible d'exporter la clé
    ['encrypt', 'decrypt'],
  );
  inMemoryKey = key;
  try {
    await set(DB_KEY_CRYPTO, key);
  } catch {
    // Si le navigateur refuse de stocker la CryptoKey (ex. structuredClone KO),
    // on conserve la clé uniquement en mémoire. La session reste valide mais
    // ne survivra pas au rechargement de l'onglet.
  }
  return key;
}

export async function saveRefreshToken(token: string): Promise<void> {
  const subtle = assertWebCryptoAvailable();
  const key = await getOrCreateKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(token);
  const cipherBuffer = await subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const payload: EncryptedPayload = {
    iv: Array.from(iv),
    ciphertext: Array.from(new Uint8Array(cipherBuffer)),
  };
  await set(DB_KEY_REFRESH, payload);
}

export async function loadRefreshToken(): Promise<string | null> {
  try {
    const subtle = assertWebCryptoAvailable();
    const payload = (await get(DB_KEY_REFRESH)) as EncryptedPayload | undefined;
    if (!payload || !Array.isArray(payload.iv) || !Array.isArray(payload.ciphertext)) {
      return null;
    }
    const key = await getOrCreateKey();
    const iv = new Uint8Array(payload.iv);
    const ciphertext = new Uint8Array(payload.ciphertext);
    const plainBuffer = await subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return new TextDecoder().decode(plainBuffer);
  } catch {
    // Clé absente, corruption, mauvaise origine : on retourne null silencieusement.
    return null;
  }
}

export async function clearRefreshToken(): Promise<void> {
  try {
    await del(DB_KEY_REFRESH);
  } catch {
    // Best-effort : on ignore les erreurs IndexedDB.
  }
}

/** Réinitialise complètement le coffre (utile pour les tests). */
export async function resetSecureStore(): Promise<void> {
  inMemoryKey = null;
  try {
    await del(DB_KEY_REFRESH);
    await del(DB_KEY_CRYPTO);
  } catch {
    // ignore
  }
}
